import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Square, Truck, FileText } from 'lucide-react';
import { printInvoice } from '../../utils/printInvoice';

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Packing', color: 'bg-yellow-100 text-yellow-700' },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Complete', color: 'bg-green-100 text-green-700' },
};

const OrderCard = ({ order, index, isSelected, isNew, updatingOrder, onToggleSelect, onStatusClick }) => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`p-6 rounded-2xl border bg-background/50 transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-border'} ${isNew ? 'ring-2 ring-amber-400 border-amber-300' : ''}`}
      data-testid={`order-${order.order_id}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <button onClick={() => onToggleSelect(order.order_id)} className="flex-shrink-0" data-testid={`select-order-${order.order_id}`}>
            {isSelected
              ? <CheckSquare className="w-5 h-5 text-primary" />
              : <Square className="w-5 h-5 text-muted-foreground" />}
          </button>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Order #{order.order_id}
              {isNew && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-amber-900 animate-pulse" data-testid={`new-badge-${order.order_id}`}>NEW</span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground">
              {new Date(order.created_at).toLocaleDateString('en-AU', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4 mt-2 md:mt-0">
          <button onClick={() => printInvoice(order)}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-border text-foreground/70 hover:text-primary hover:border-primary transition-colors"
            data-testid={`print-invoice-${order.order_id}`}>
            <FileText className="w-4 h-4" /><span className="text-xs font-medium">Invoice</span>
          </button>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${order.payment_method === 'stripe' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {order.payment_method === 'stripe' ? 'Card (Stripe)' : 'PayPal'}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${order.shipping_method === 'pickup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {order.shipping_method === 'pickup' ? 'Pickup' : 'Postage'}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${(STATUS_CONFIG[order.status] || STATUS_CONFIG.confirmed).color}`} data-testid={`status-badge-${order.order_id}`}>
            {(STATUS_CONFIG[order.status] || STATUS_CONFIG.confirmed).label}
          </span>
        </div>
      </div>

      {/* Status Update Controls */}
      <div className="flex items-center space-x-2 mb-4 p-3 rounded-xl bg-muted/30 border border-border">
        <span className="text-xs font-medium text-foreground/70 mr-2">Update:</span>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => onStatusClick(order.order_id, key)}
            disabled={order.status === key || updatingOrder === order.order_id}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              order.status === key
                ? cfg.color + ' ring-2 ring-offset-1 ring-current'
                : 'bg-background border border-border text-foreground/60 hover:bg-muted hover:text-foreground'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`set-status-${key}-${order.order_id}`}>
            {updatingOrder === order.order_id ? '...' : cfg.label}
          </button>
        ))}
      </div>

      {/* Tracking Number Display */}
      {order.tracking_number && (
        <div className="flex items-center space-x-3 mb-4 p-3 rounded-xl bg-purple-50 border border-purple-200">
          <Truck className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-xs text-purple-600 font-medium">Tracking Number</p>
            <p className="text-sm font-bold text-foreground">{order.tracking_number}</p>
          </div>
          {order.carrier && (
            <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">{order.carrier}</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 rounded-xl bg-muted/20">
        <div>
          <p className="text-xs text-muted-foreground">Customer</p>
          <p className="text-sm font-medium text-foreground">{order.customer_name || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Email</p>
          <p className="text-sm font-medium text-foreground">{order.customer_email || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Phone</p>
          <p className="text-sm font-medium text-foreground">{order.customer_phone || 'N/A'}</p>
        </div>
        {order.shipping_address && (
          <div className="md:col-span-3">
            <p className="text-xs text-muted-foreground">{order.shipping_method === 'pickup' ? 'Pickup Location' : 'Shipping Address'}</p>
            <p className="text-sm font-medium text-foreground">
              {order.shipping_address.pickup_location
                ? order.shipping_address.pickup_location
                : [order.shipping_address.street, order.shipping_address.street2, [order.shipping_address.suburb, order.shipping_address.state, order.shipping_address.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-border pt-4">
        <div className="space-y-2">
          {order.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-foreground">{item.name} ({item.size}) x {item.quantity}</span>
              <span className="text-foreground font-medium">${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-border mt-3 pt-3 space-y-1">
          <div className="flex justify-between text-sm text-foreground/70">
            <span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount ({order.promo_code || 'BEE10'})</span><span>-${order.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-foreground/70">
            <span>Shipping</span><span>{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-foreground">
            <span>Total</span><span>${order.total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export { STATUS_CONFIG };
export default OrderCard;
